import React, { useState, useEffect } from "react"
import { useTheme } from "@mui/material/styles";
import { useThemeMode } from "../context/ThemeModeContext";
import {
    Box,
    Stack,
    Typography,
    Button,
    Chip,
    IconButton,
    Alert,
    Snackbar,
    AppBar,
    Toolbar,
    CircularProgress,
    Menu,
    MenuItem,
} from "@mui/material"
import {
    Notifications as NotificationsIcon,
    Restaurant as RestaurantIcon,
    MeetingRoom as CabinIcon,
    NoteAdd as NotesIcon,
    CheckCircle as CheckCircleIcon,
} from "@mui/icons-material"
import { format, addDays, addMinutes } from "date-fns"
import { useNavigate } from "react-router-dom"
import { GetPollWithUserId, GetPollWithUserIdPayload, SendNoteToEmail } from "../utils/APIRequest"
import { GetCabins, GetAvailability, CreateBooking, Cabin, Booking, CancelBooking } from "../utils/CabinBookingAPIRequest"
import { User } from "./MainScreen"
import { STORAGE_KEYS } from "../utils/StorageKeys"
import { isVotingAllowed } from "../utils/timeUtils"
import { Logo } from "../components/layout/Topbar"
import CollapsibleCard from "../components/CollapsibleCard";
import CardHeaderAvatar from "../components/CardHeaderAvatar";
import BulletinBoard from "../components/MobileHomeScreen/BulletinBoard";
import LunchPollCard from "../components/MobileHomeScreen/LunchPollCard";
import NotesToSelfCard from "../components/MobileHomeScreen/NotesToSelfCard";
import { BulletinItem, GetBulletins } from "../utils/BulletinBoardAPIRequest";
import { AccountCircle } from "@mui/icons-material";
import { useUserContext } from "../context/UserContext";
import SettingsIcon from "@mui/icons-material/Settings";


// Types
interface TimeSlot {
    time: string
    display: string
}

interface PollResponse {
    emp_name: string
    foodChoice: string
    pollDate: string
    username: string
}

// Generate 30-min slots for today only
const generateToday30MinSlots = (): TimeSlot[] => {
    const slots: TimeSlot[] = []
    const startTime = new Date()
    startTime.setHours(9, 0, 0, 0)
    for (let i = 0; i < 18; i++) { // 9:00 to 18:00, 30-min slots
        const time = addMinutes(startTime, i * 30)
        slots.push({
            time: format(time, "HH:mm"),
            display: format(time, "h:mm a"),
        })
    }
    return slots
}

const today30MinSlots = generateToday30MinSlots()


export default function MobileHomeScreen() {
    const navigate = useNavigate()
    // Theme and dark mode context
    const theme = useTheme();
    const { isDarkMode, toggleDarkMode } = useThemeMode();
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const isMenuOpen = Boolean(menuAnchorEl);

    // Account menu handlers
    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setMenuAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };
    const handleChangePassword = () => {
        handleMenuClose();
        navigate("/change-password");
    };

    // Icons for theme toggle
    const SunIcon = (props: any) => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...props}>
            <circle cx="12" cy="12" r="5" fill="#FFD600" />
            <g stroke="#FFD600" strokeWidth="2">
                <line x1="12" y1="2" x2="12" y2="5" />
                <line x1="12" y1="19" x2="12" y2="22" />
                <line x1="2" y1="12" x2="5" y2="12" />
                <line x1="19" y1="12" x2="22" y2="12" />
                <line x1="4.22" y1="4.22" x2="6.34" y2="6.34" />
                <line x1="17.66" y1="17.66" x2="19.78" y2="19.78" />
                <line x1="4.22" y1="19.78" x2="6.34" y2="17.66" />
                <line x1="17.66" y1="6.34" x2="19.78" y2="4.22" />
            </g>
        </svg>
    );
    const MoonIcon = (props: any) => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...props}>
            <path d="M21 12.79A9 9 0 0 1 12.79 3a7 7 0 1 0 8.21 9.79z" fill="#FFD600" />
        </svg>
    );

    // State management
    const [currentUser, setCurrentUser] = useState<User | null>(null)
    const [loading, setLoading] = useState(true)
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" as "success" | "error" })

    // Bulletin state
    const [bulletinMessages, setBulletinMessages] = useState<BulletinItem[]>([])

    // Lunch poll state
    const [userHasVoted, setUserHasVoted] = useState(false)

    // Cabin booking state
    const [cabins, setCabins] = useState<Cabin[]>([])
    const [bookings, setBookings] = useState<Booking[]>([])
    const [cabinDialogOpen, setCabinDialogOpen] = useState(false)
    // For new UI: selected cabin for slot view
    const [selectedCabinId, setSelectedCabinId] = useState<string | null>(null)

    // Notes state
    const [noteText, setNoteText] = useState("")
    const [isRecording, setIsRecording] = useState(false)
    const [notesDialogOpen, setNotesDialogOpen] = useState(false)
    const [noteType, setNoteType] = useState<"text" | "voice" | "image">("text")
    const [selectedImage, setSelectedImage] = useState<File | null>(null)
    const { handleLogout } = useUserContext();

    // Get next 3 working days including today
    const getWorkingDays = () => {
        const dates: Date[] = []
        let currentDate = new Date()

        while (dates.length < 3) {
            const dayOfWeek = currentDate.getDay()
            if (dayOfWeek >= 1 && dayOfWeek <= 5) {
                dates.push(new Date(currentDate))
            }
            currentDate = addDays(currentDate, 1)
        }
        return dates
    }

    const workingDays = getWorkingDays()

    // Load initial data
    useEffect(() => {
        loadInitialData()
    }, [])

    const loadInitialData = async () => {
        try {
            setLoading(true)

            // Load user from storage
            const user = localStorage.getItem(STORAGE_KEYS.USER)
            if (user) {
                const parsedUser: User = JSON.parse(user)
                setCurrentUser(parsedUser)

                // Load user's poll data
                await fetchCurrentUserPoll(parsedUser)
            }

            // Load bulletin messages
            await loadBulletinMessages()

            // Load cabin data
            await loadCabinData()

        } catch (error) {
            console.error("Error loading initial data:", error)
            setSnackbar({ open: true, message: "Error loading data", severity: "error" })
        } finally {
            setLoading(false)
        }
    }

    const loadBulletinMessages = async () => {
        try {
            const response = await GetBulletins()
            console.log("Bulletin messages response:", response)
            if (response && Array.isArray(response)) {
                setBulletinMessages(response)
            }
        } catch (error) {
            console.error("Error loading bulletin messages:", error)
            // Keep mock data if API fails
        }
    }

    const fetchCurrentUserPoll = async (user: User) => {
        try {
            const tomorrow = new Date()
            tomorrow.setDate(tomorrow.getDate() + 1)
            const pollDate = tomorrow.toISOString().split("T")[0]

            const pollPayload: GetPollWithUserIdPayload = {
                date: pollDate,
                username: user.id
            }

            const response = await GetPollWithUserId(pollPayload)
            if (response && response.length > 0) {
                setUserHasVoted(true)
            }
        } catch (error) {
            console.error("Error fetching user poll:", error)
        }
    }

    const loadCabinData = async () => {
        try {
            // Load cabins
            const cabinsResponse = await GetCabins()
            if (cabinsResponse?.status === "success") {
                setCabins(cabinsResponse.data.cabins)
            }

            // Load availability for next 3 days
            const startDate = format(workingDays[0], "yyyy-MM-dd")
            const endDate = format(workingDays[2], "yyyy-MM-dd")

            const availabilityResponse = await GetAvailability({ startDate, endDate })
            console.log("Cabin availability response:", availabilityResponse)
            if (availabilityResponse?.status === "success") {
                const normalizedBookings = availabilityResponse.data.bookings.map((booking: any) => ({
                    ...booking,
                    timeSlot: booking.timeSlot?.substring(0, 5) || booking.timeSlot,
                    status: booking.status || "active"
                }))
                console.log("Normalized bookings:", normalizedBookings)
                setBookings(normalizedBookings)
            }
        } catch (error) {
            console.error("Error loading cabin data:", error)
        }
    }

    // Check if cabin is booked for today
    const isCabinBooked = (timeSlot: string, cabinId: string) => {
        const todayStr = format(new Date(), "yyyy-MM-dd")
        return bookings.some((booking) =>
            booking.date === todayStr &&
            booking.timeSlot === timeSlot &&
            booking.cabinId === cabinId &&
            booking.status === "active"
        )
    }

    // Get available cabins for today (at least one slot available)
    const getAvailableCabinsToday = () => {
        return cabins.filter(cabin => today30MinSlots.some(slot => !isCabinBooked(slot.time, cabin.id)))
    }

    // Get available slots for a selected cabin (today)
    const getAvailableSlotsForCabin = (cabinId: string) => {
        return today30MinSlots.filter(slot => !isCabinBooked(slot.time, cabinId))
    }

    // Book a slot for a cabin (no popup)
    const handleCabinBook = async (cabinId: string, slotTime: string) => {
        if (!currentUser) return
        try {
            const response = await CreateBooking({
                username: currentUser.id,
                cabinId: cabinId,
                date: format(new Date(), "yyyy-MM-dd"),
                timeSlot: slotTime,
                title: "Meeting",
            })
            if (response?.status === "success") {
                setSnackbar({ open: true, message: "Cabin booked successfully!", severity: "success" })
                await loadCabinData()
            } else {
                throw new Error(response?.data?.message || "Booking failed")
            }
        } catch (error: any) {
            console.error("Booking error:", error)
            setSnackbar({ open: true, message: error.message || "Error booking cabin", severity: "error" })
        }
    }

    const handleLunchPollResponse = (response: PollResponse) => {
        console.log('Lunch poll response:', response);
        if (response.foodChoice) {
            setSnackbar({ open: true, message: "Poll submitted successfully!", severity: "success" });
        } else {
            setSnackbar({ open: true, message: "Failed to submit poll", severity: "error" });
        }
    }

    const getUserBookingForSlot = (timeSlot: string, cabinId: string) => {
        if (!currentUser) return null;
        const todayStr = format(new Date(), "yyyy-MM-dd");
        console.log("Checking bookings for:", todayStr, timeSlot, cabinId);
        console.log("bookings:", bookings);
        return bookings.find(
            (booking) =>
                booking.date === todayStr &&
                booking.timeSlot === timeSlot &&
                booking.cabinId === cabinId &&
                booking.status === "active" &&
                booking.username === currentUser.id
        );
    };

    // Remove a booking by id
    const handleRemoveBooking = async (bookingId: string) => {
        if (!currentUser) return null;
        try {
            const response = await CancelBooking(bookingId, { username: currentUser.id });
            if (response?.status === "success") {
                setSnackbar({ open: true, message: "Booking cancelled!", severity: "success" });
                await loadCabinData();
            } else {
                throw new Error(response?.data?.message || "Cancellation failed");
            }
        } catch (error: any) {
            setSnackbar({ open: true, message: error.message || "Error cancelling booking", severity: "error" });
        }
    };


    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
                <CircularProgress size={60} />
            </Box>
        )
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
            {/* Main Content - avoid scrolling the entire page, only content area scrolls if needed */}
            <Box sx={{ pt: 2, pb: 8, flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                {/* Collapsible Cards */}
                <Stack spacing={3} sx={{ flex: 1, minHeight: 0, overflow: 'auto', pb: 2 }}>
                    {/* 1. Bulletin Board */}
                    <CollapsibleCard
                        title={<Stack direction="row" alignItems="center" spacing={1}>
                            <CardHeaderAvatar icon={<NotificationsIcon sx={{ color: '#fff' }} />} avatarBg={theme.palette.info.light} />
                            <span>Bulletin Board</span>
                        </Stack>}
                        defaultExpanded={true}
                        action={<Chip label={bulletinMessages.length} color="primary" size="small" />}
                    >
                        <BulletinBoard bulletinMessages={bulletinMessages} />
                    </CollapsibleCard>

                    {/* 2. Lunch Poll */}
                    <CollapsibleCard
                        title={<Stack direction="row" alignItems="center" spacing={1}>
                            <CardHeaderAvatar icon={<RestaurantIcon sx={{ color: '#fff' }} />} avatarBg={theme.palette.primary.light} />
                            <span>Lunch Poll</span>
                        </Stack>}
                        defaultExpanded={false}
                        action={
                            userHasVoted ?
                                <Chip icon={<CheckCircleIcon />} label="Submitted" color="success" size="small" /> :
                                <Chip label="Pending" color="warning" size="small" />
                        }
                    >
                        <LunchPollCard
                            isVotingAllowed={isVotingAllowed}
                            onPollSubmit={(response) => {
                                handleLunchPollResponse(response);
                            }}
                        />
                    </CollapsibleCard>

                    {/* 3. Cabin Booking - New Mobile UI */}
                    <CollapsibleCard
                        title={<Stack direction="row" alignItems="center" spacing={1}>
                            <CardHeaderAvatar icon={<CabinIcon sx={{ color: '#fff' }} />} avatarBg={theme.palette.success.light} />
                            <span>Let's Meet</span>
                        </Stack>}
                        defaultExpanded={false}
                        action={<Chip label={`${cabins.length} rooms`} color="primary" size="small" />}
                    >
                        <Box>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Available Rooms</Typography>
                            <Box sx={{ mb: 1 }}>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: {
                                            xs: 'repeat(2, 1fr)',
                                            sm: 'repeat(3, 1fr)',
                                        },
                                        gap: 1,
                                    }}
                                >
                                    {getAvailableCabinsToday().map(cabin => (
                                        <Box
                                            key={cabin.id}
                                            sx={{
                                                px: 2,
                                                py: 1.5,
                                                borderRadius: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                border: selectedCabinId === cabin.id ? `2px solid ${theme.palette.getContrastText(cabin.color || theme.palette.grey[200])}` : '2px solid transparent',
                                                background: cabin.color || theme.palette.grey[200],
                                                color: theme.palette.getContrastText(cabin.color || theme.palette.grey[200]),
                                                fontWeight: 400,
                                                fontSize: 16,
                                                boxShadow: selectedCabinId === cabin.id ? 3 : 1,
                                                transition: 'all 0.2s',
                                            }}
                                            onClick={() => setSelectedCabinId(cabin.id)}
                                        >
                                            <span>{cabin.name}</span>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                            {selectedCabinId && (
                                <>
                                    <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>Available Slots</Typography>
                                    {/* Show all slots: available, user-booked, others-booked */}
                                    <Box sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(3, 1fr)',
                                        gap: 1,
                                        pb: 1,
                                    }}>
                                        {today30MinSlots.map(slot => {
                                            const userBooking = getUserBookingForSlot(slot.time, selectedCabinId);
                                            const isBooked = isCabinBooked(slot.time, selectedCabinId);
                                            const cabin = cabins.find(c => c.id === selectedCabinId);
                                            if (userBooking) {
                                                // Slot booked by current user
                                                return (
                                                    <Button
                                                        key={slot.time}
                                                        variant="contained"
                                                        sx={{
                                                            borderRadius: 2,
                                                            fontWeight: 400,
                                                            width: '100%',
                                                            color: theme.palette.success.contrastText,
                                                            background: cabin?.color || theme.palette.background.default,
                                                            borderColor: cabin?.color || theme.palette.success.light,
                                                            border: '2px solid',
                                                            opacity: 1,
                                                        }}
                                                        onClick={() => handleRemoveBooking(userBooking._id)}
                                                    >
                                                        {slot.display}
                                                    </Button>
                                                );
                                            } else if (!isBooked) {
                                                // Available slot
                                                return (
                                                    <Button
                                                        key={slot.time}
                                                        variant="outlined"
                                                        sx={{
                                                            borderRadius: 2,
                                                            borderWidth: 2,
                                                            fontWeight: 400,
                                                            width: '100%',
                                                            background: theme.palette.background.default,
                                                            color: theme.palette.text.primary,
                                                            borderColor: cabin?.color || theme.palette.success.light,
                                                            // opacity: 0.85,
                                                        }}
                                                        onClick={() => handleCabinBook(selectedCabinId, slot.time)}
                                                    >
                                                        {slot.display}
                                                    </Button>
                                                );
                                            } else {
                                                // Booked by someone else
                                                return (
                                                    <Button
                                                        key={slot.time}
                                                        variant="outlined"
                                                        disabled
                                                        sx={{
                                                            borderRadius: 2,
                                                            fontWeight: 400,
                                                            width: '100%',
                                                        }}
                                                    >
                                                        {slot.display}
                                                    </Button>
                                                );
                                            }
                                        })}
                                    </Box>
                                </>
                            )}
                        </Box>
                    </CollapsibleCard>


                    {/* 4. Notes to Self */}
                    <CollapsibleCard
                        title={<Stack direction="row" alignItems="center" spacing={1}>
                            <CardHeaderAvatar icon={<NotesIcon sx={{ color: '#fff' }} />} avatarBg={theme.palette.secondary.light} />
                            <span>Notes to Self</span>
                            <IconButton
                                aria-label="settings"
                                size="medium"
                                sx={{ mr: 1 }}
                                onClick={() => navigate("/noteToSelf_settings")}
                            >
                                <SettingsIcon />
                            </IconButton>
                        </Stack>}
                        defaultExpanded={false}
                    >
                        <Stack direction="row" spacing={2} justifyContent="space-around" alignItems="center" sx={{ py: 2 }}>
                            <Button
                                variant="contained"
                                sx={{ backgroundColor: "#f44336", px: 3 }}
                                onClick={() => navigate("/noteToSelf_text")}
                            >
                                Text
                            </Button>
                            <Button
                                variant="contained"
                                sx={{ backgroundColor: "#ff9800", px: 3 }}
                                onClick={() => navigate("/noteToSelf_voice")}
                            >
                                Voice
                            </Button>
                            <Button
                                variant="contained"
                                sx={{ backgroundColor: "#2196f3", px: 3 }}
                                onClick={() => navigate("/noteToSelf_image")}
                            >
                                Image
                            </Button>

                        </Stack>
                    </CollapsibleCard>
                </Stack>
            </Box>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box >
    )
}
